import { clsx } from 'clsx';
import { Edit3, Lock } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { StatusBadge } from '../common/StatusBadge';
import type { Decision } from '../../types';

interface EvaluationSummaryCardProps {
  /** Unique identifier for this evaluation ('human', 'ai', or evaluator_id) */
  evaluationId: string;
  /** Display label (e.g., 'YOUR', 'AI', evaluator name) */
  label: string;
  /** Total score out of 100 */
  score: number;
  /** Decision badge type */
  decision: Decision;
  /** Number of red flags identified */
  redFlagCount: number;
  /** Whether this card's details are currently expanded */
  isExpanded: boolean;
  /** Whether the card is locked (before user submits their evaluation) */
  isLocked?: boolean;
  /** Whether to show Edit button (only for YOUR evaluation) */
  showEditButton?: boolean;
  /** Callback when View/Hide Details is clicked */
  onToggleDetails: (evaluationId: string) => void;
  /** Callback when Edit is clicked */
  onEdit?: () => void;
}

export function EvaluationSummaryCard({
  evaluationId,
  label,
  score,
  decision,
  redFlagCount,
  isExpanded,
  isLocked = false,
  showEditButton = false,
  onToggleDetails,
  onEdit,
}: EvaluationSummaryCardProps) {
  const scoreColor = getScoreColor(score);

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-action font-mono text-sm">{label}</span>
          <span className="text-wolf-300 font-medium">Evaluation</span>
        </div>
        <StatusBadge status={decision} />
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className={clsx('font-mono text-3xl font-bold', scoreColor)}>
          {score.toFixed(1)}
        </span>
        <span className="text-wolf-500">/100</span>
      </div>

      {redFlagCount > 0 && (
        <div className="text-red-400 text-sm mb-4">
          {redFlagCount} red flag(s) identified
        </div>
      )}

      <div className="flex gap-2 mt-4">
        {isLocked ? (
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            disabled
            title="Submit your evaluation to view details"
          >
            <Lock className="w-4 h-4 mr-2" />
            Locked
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={() => onToggleDetails(evaluationId)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? 'Hide Details' : 'View Details'}
          </Button>
        )}

        {showEditButton && (
          <Button
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={onEdit}
          >
            <Edit3 className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
      </div>
    </Card>
  );
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-action';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}
